// 全局WebSocket管理器 - 优化版

import { tokenStorage } from '~/utils/storage'
import { showTopNotification } from '@/composables/useTopNotification'

class WebSocketManager {
  private ws: WebSocket | null = null
  private isConnected = false
  private isConnecting = false
  private connectionError: string | null = null
  private reconnectCount = 0
  private reconnectTimer: NodeJS.Timeout | null = null
  private heartbeatTimer: NodeJS.Timeout | null = null
  private manualClose = false
  private lastPong = Date.now()
  private connectionQuality: 'excellent' | 'good' | 'poor' | 'disconnected' = 'disconnected'
  private messagesSent = 0
  private messagesReceived = 0

  private readonly baseUrl: string
  private readonly reconnectInterval = 1000 // iOS 优化：快速重连（1秒）
  private readonly heartbeatInterval = 5000 // iOS 优化：更频繁的心跳（3秒）
  private readonly heartbeatTimeout = 16000 // iOS 优化：更短的超时（16秒）
  private maxReconnectInterval = 10000 // 最大重连间隔
  private currentReconnectDelay = 1000 // 当前重连延迟（指数退避）

  private callbacks: Record<'open' | 'message' | 'error' | 'close', Function[]> = {
    open: [],
    message: [],
    error: [],
    close: []
  }

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private getWebSocketUrl(): string {
    const token = tokenStorage.getToken()
    return `${this.baseUrl}?token=${token || 'e5f8297389e2ae33f6417d2b908da160'}`
  }

  on(event: 'open' | 'message' | 'error' | 'close', callback: Function) {
    this.callbacks[event].push(callback)
  }

  off(event: 'open' | 'message' | 'error' | 'close', callback: Function) {
    this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback)
  }

  once(event: 'open' | 'message' | 'error' | 'close', callback: Function) {
    const handler = (data: any) => {
      this.off(event, handler)
      callback(data)
    }
    this.on(event, handler)
  }

  private emit(event: 'open' | 'message' | 'error' | 'close', data: any) {
    this.callbacks[event].forEach(cb => cb(data))
  }

  connect(): void {
    // 🔧 iOS 修复：检查实际连接状态，而不仅仅是标志位
    const actualState = this.ws?.readyState
    const isActuallyConnected = actualState === WebSocket.OPEN
    const isActuallyConnecting = actualState === WebSocket.CONNECTING

    // 如果标志位和实际状态不一致，强制同步
    if (this.isConnected && !isActuallyConnected) {
      console.warn('⚠️ [iOS修复] 状态不一致：标志显示已连接但实际未连接，强制重置')
      this.isConnected = false
      this.isConnecting = false
    }

    if (this.isConnecting && !isActuallyConnecting) {
      console.warn('⚠️ [iOS修复] 状态不一致：标志显示连接中但实际未在连接，强制重置')
      this.isConnecting = false
    }

    // 防止重复连接
    if ((this.isConnecting && isActuallyConnecting) || (this.isConnected && isActuallyConnected)) {
      console.log('🔌 WebSocket已在连接中，跳过', {
        isConnecting: this.isConnecting,
        isConnected: this.isConnected,
        actualState: actualState
      })
      return
    }

    // 🔧 关键修复：在创建新连接前，先完全清理旧的连接
    if (this.ws) {
      const oldState = this.ws.readyState
      console.log(`🧹 清理旧连接 (状态: ${oldState})`)
      
      // 移除旧连接的所有事件监听器，避免旧连接的事件继续触发
      try {
        this.ws.onopen = null
        this.ws.onmessage = null
        this.ws.onerror = null
        this.ws.onclose = null
      } catch (e) {
        console.warn('⚠️ 移除旧连接事件监听器失败:', e)
      }
      
      // 关闭旧连接（如果还没关闭）
      if (oldState === WebSocket.OPEN || oldState === WebSocket.CONNECTING) {
        try {
          this.ws.close(1000, '创建新连接')
          console.log('✅ 已关闭旧连接')
        } catch (e) {
          console.warn('⚠️ 关闭旧连接失败:', e)
        }
      }
      
      // 清空引用
      this.ws = null
    }

    console.log(`🔌 开始连接... (第 ${this.reconnectCount + 1} 次)`, {
      manualClose: this.manualClose,
      isConnecting: this.isConnecting,
      isConnected: this.isConnected
    })

    // 🔧 iOS 修复：设置连接超时保护（10秒）
    let connectionTimeout: NodeJS.Timeout | null = null

    try {
      this.isConnecting = true
      this.connectionError = null
      this.manualClose = false

      this.ws = new WebSocket(this.getWebSocketUrl())

      // 如果10秒内没有连接成功，强制触发重连
      connectionTimeout = setTimeout(() => {
        if (this.isConnecting && this.ws?.readyState !== WebSocket.OPEN) {
          console.error('❌ [iOS修复] 连接超时（10秒未成功），强制触发重连')
          this.isConnecting = false
          this.isConnected = false
          
          // 强制关闭旧连接
          if (this.ws) {
            try {
              this.ws.close()
            } catch (e) {
              console.warn('关闭超时连接失败:', e)
            }
          }
          
          // 触发重连
          if (!this.manualClose) {
            this.scheduleReconnect()
          }
        }
      }, 10000)

      this.ws.onopen = event => {
        // 清除连接超时定时器
        if (connectionTimeout) {
          clearTimeout(connectionTimeout)
          connectionTimeout = null
        }
        console.log('✅ WebSocket连接成功')
        this.isConnected = true
        this.isConnecting = false
        this.reconnectCount = 0
        this.currentReconnectDelay = this.reconnectInterval // 🔧 重置退避延迟
        this.connectionError = null

        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer)
          this.reconnectTimer = null
        }

        this.startHeartbeat()
        this.emit('open', event)
      }

      this.ws.onmessage = event => {
        let data: any
        try {
          data = JSON.parse(event.data)
        } catch {
          data = event.data
        }

        this.lastPong = Date.now()
        this.messagesReceived++ // 🔧 iOS 优化：统计接收消息数

        // 心跳包处理
        if (data?.type === 1 || data?.msg === 'heart beat') {
          this.send({ type: 'pong', msg: Date.now() })
          console.log('💓 [iOS优化] 收到服务端心跳，已回复 pong')
          return
        }

        // 通知消息
        if (data?.type === 41) {
          showTopNotification({
            avatar: data.avatar,
            title: data.name,
            description: data.content,
            duration: 2000
          })
        }

        this.emit('message', data)
      }

      this.ws.onerror = event => {
        console.error('❌ WebSocket错误', {
          readyState: this.ws?.readyState,
          timestamp: new Date().toISOString(),
          reconnectCount: this.reconnectCount,
          isConnecting: this.isConnecting,
          isConnected: this.isConnected
        })

        this.connectionError = 'WebSocket连接错误'
        this.isConnecting = false
        this.isConnected = false

        this.saveErrorLog('error', { 
          timestamp: new Date().toISOString(),
          reconnectCount: this.reconnectCount,
          readyState: this.ws?.readyState
        })
        this.emit('error', event)
        
        // 🔧 iOS 修复：确保错误后会触发重连
        console.log('⚠️ [iOS修复] WebSocket错误，等待 onclose 触发重连')
      }

      this.ws.onclose = event => {
        // 清除连接超时定时器
        if (connectionTimeout) {
          clearTimeout(connectionTimeout)
          connectionTimeout = null
        }
        
        const closeInfo = {
          code: event.code,
          reason: event.reason || '未知',
          wasClean: event.wasClean,
          reconnectCount: this.reconnectCount,
          isConnecting: this.isConnecting,
          isConnected: this.isConnected,
          manualClose: this.manualClose
        }

        console.log('🔌 WebSocket关闭', closeInfo)

        this.isConnected = false
        this.isConnecting = false
        this.stopHeartbeat()

        // 更新错误信息
        const closeMessages: Record<number, string> = {
          1000: '正常关闭',
          1001: '端点离开',
          1006: '异常关闭',
          1011: '服务器错误'
        }
        this.connectionError = closeMessages[event.code] || `连接关闭 (${event.code})`

        this.saveErrorLog('close', closeInfo)

        // 自动重连
        if (!this.manualClose) {
          // iOS后台断开，重置计数
          if (event.code === 1001 || event.code === 1006) {
            this.reconnectCount = 0
          }
          console.log('🔄 准备重连...')
          this.scheduleReconnect()
        }

        this.emit('close', event)
      }
    } catch (error) {
      // 清除连接超时定时器
      if (connectionTimeout) {
        clearTimeout(connectionTimeout)
        connectionTimeout = null
      }
      
      console.error('❌ 创建WebSocket失败:', error)

      this.connectionError = `创建连接失败: ${error instanceof Error ? error.message : String(error)}`
      this.isConnecting = false
      this.isConnected = false

      this.saveErrorLog('connect', { 
        error: String(error),
        reconnectCount: this.reconnectCount
      })

      // 重连
      if (!this.manualClose) {
        console.log('🔄 创建失败，准备重连...')
        this.scheduleReconnect()
      }
    }
  }

  disconnect(): void {
    this.manualClose = true

    // 清理重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    // 停止心跳
    this.stopHeartbeat()
    

    // 🔧 关键修复：完全清理 WebSocket 连接
    if (this.ws) {
      // 移除所有事件监听器
      try {
        this.ws.onopen = null
        this.ws.onmessage = null
        this.ws.onerror = null
        this.ws.onclose = null
      } catch (e) {
        console.warn('⚠️ 移除事件监听器失败:', e)
      }
      
      // 关闭连接
      try {
        this.ws.close(1000, '主动断开')
      } catch (e) {
        console.warn('⚠️ 关闭连接失败:', e)
      }
      
      // 清空引用
      this.ws = null
    }

    // 重置状态
    this.isConnected = false
    this.isConnecting = false
    this.reconnectCount = 0
  }

  destroy(): void {
    this.disconnect()
    this.callbacks = { open: [], message: [], error: [], close: [] }
  }

  send(data: any): boolean {
    if (!this.isConnected || !this.ws) return false

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data)
      this.ws.send(message)
      this.messagesSent++ // 🔧 iOS 优化：统计发送消息数
      return true
    } catch {
      return false
    }
  }

  sendChatMessage(content: string, roleId?: string): boolean {
    return this.send({
      type: 'chat',
      data: {
        content: content.trim(),
        roleId: roleId || 'default',
        timestamp: Date.now()
      }
    })
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      console.log('🔄 重连已在计划中')
      return
    }

    if (this.manualClose) {
      return
    }

    this.reconnectCount++

    // 🔧 iOS 优化：智能重连延迟（指数退避 + 网络检测）
    let delay = this.reconnectInterval

    // 检查网络状态
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true

    if (!isOnline) {
      // 离线状态，延长等待时间
      delay = 5000
      console.log(`⏰ [iOS优化] 网络离线，${delay / 1000}秒后重连 (第 ${this.reconnectCount} 次)`)
    } else if (this.reconnectCount <= 3) {
      // 前3次快速重连（iOS 网络波动通常很快恢复）
      delay = 1000
      console.log(`⏰ [iOS优化] 快速重连，${delay / 1000}秒后重连 (第 ${this.reconnectCount} 次)`)
    } else if (this.reconnectCount <= 10) {
      // 4-10次使用指数退避，但有上限
      delay = Math.min(this.currentReconnectDelay * 1.5, this.maxReconnectInterval)
      this.currentReconnectDelay = delay
      console.log(
        `⏰ [iOS优化] 指数退避重连，${delay / 1000}秒后重连 (第 ${this.reconnectCount} 次)`
      )
    } else {
      // 超过10次后，使用固定间隔
      delay = this.maxReconnectInterval
      console.log(
        `⏰ [iOS优化] 固定间隔重连，${delay / 1000}秒后重连 (第 ${this.reconnectCount} 次)`
      )
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null

      if (!this.manualClose) {
        // 强制重置状态
        this.isConnecting = false
        this.isConnected = false
      }
      this.connect()
    }, delay)
  }

  /**
   * 🔧 启动心跳机制
   * 职责：
   * 1. 每3秒主动发送心跳包，保持连接活跃
   * 2. 监测10秒未收到回复则触发重连（主要的超时检测机制）
   * 3. 实时更新连接质量
   */
  private startHeartbeat(): void {
    if (this.heartbeatTimer) return

    this.lastPong = Date.now()

    // 🔧 iOS 优化：更频繁的心跳检测（每3秒）
    this.heartbeatTimer = setInterval(() => {
      if (!this.isConnected) return

      const timeSinceLastHeartbeat = Date.now() - this.lastPong

      // 🔧 iOS 优化：更新连接质量
      this.connectionQuality = this.calculateConnectionQuality()

      // 🔧 iOS 优化：10秒未收到心跳回复则断开重连（主要超时检测）
      if (timeSinceLastHeartbeat > this.heartbeatTimeout) {
        console.warn(
          `❌ [心跳检测] 心跳超时 (${Math.floor(timeSinceLastHeartbeat / 1000)}秒未收到回复，超过${this.heartbeatTimeout / 1000}秒阈值)`
        )
        console.log(`📊 [心跳检测] 连接质量: ${this.connectionQuality}`)
        console.log('🔄 [心跳检测] 触发重连...')
        this.reconnectWithNewToken()
      } else {
        // 🔧 iOS 优化：主动发送心跳包，保持连接活跃
        // 服务端收到 pong 后会回复心跳消息（type: 1），客户端在 onmessage 中会自动回复 pong
        const success = this.send({ type: 'pong', msg: Date.now() })
        if (!success) {
          console.warn('⚠️ [心跳检测] 心跳发送失败，连接可能已断开')
          this.connectionQuality = 'disconnected'
          this.reconnectWithNewToken()
        } else {
          const qualityEmoji = {
            excellent: '💚',
            good: '💛',
            poor: '🧡',
            disconnected: '❤️'
          }[this.connectionQuality]
          console.log(
            `💓 [心跳检测] 心跳正常 ${qualityEmoji} ${this.connectionQuality} (距上次: ${Math.floor(timeSinceLastHeartbeat / 1000)}秒)`
          )
        }
      }
    }, this.heartbeatInterval) // 使用配置的心跳间隔（3秒）
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }


  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      connectionError: this.connectionError,
      reconnectCount: this.reconnectCount
    }
  }

  private saveErrorLog(type: 'error' | 'close' | 'connect', info: any) {
    try {
      if (typeof window === 'undefined') return

      const logs = JSON.parse(localStorage.getItem('ws_error_logs') || '[]')
      logs.push({
        type,
        info,
        timestamp: new Date().toISOString(),
        reconnectCount: this.reconnectCount
      })

      // 只保留最近20条
      if (logs.length > 20) logs.shift()

      localStorage.setItem('ws_error_logs', JSON.stringify(logs))
    } catch (e) {
      // 忽略
    }
  }

  getErrorLogs(): any[] {
    try {
      if (typeof window === 'undefined') return []
      return JSON.parse(localStorage.getItem('ws_error_logs') || '[]')
    } catch {
      return []
    }
  }

  clearErrorLogs() {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('ws_error_logs')
      }
    } catch {}
  }

  isReallyConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this.isConnected
  }

  /**
   * 🔧 iOS 优化：计算连接质量
   */
  private calculateConnectionQuality(): 'excellent' | 'good' | 'poor' | 'disconnected' {
    if (!this.isConnected) return 'disconnected'

    const timeSinceLastPong = Date.now() - this.lastPong

    // 根据心跳延迟判断连接质量
    if (timeSinceLastPong < 4000) return 'excellent' // 4秒内：优秀
    if (timeSinceLastPong < 7000) return 'good' // 7秒内：良好
    return 'poor' // 超过7秒：较差
  }

  /**
   * 🔧 iOS 优化：获取连接质量
   */
  getConnectionQuality(): 'excellent' | 'good' | 'poor' | 'disconnected' {
    return this.connectionQuality
  }

  /**
   * 🔧 iOS 优化：获取连接统计信息
   */
  getConnectionStats() {
    return {
      quality: this.connectionQuality,
      messagesSent: this.messagesSent,
      messagesReceived: this.messagesReceived,
      reconnectCount: this.reconnectCount,
      timeSinceLastPong: Date.now() - this.lastPong,
      isConnected: this.isConnected,
      isConnecting: this.isConnecting
    }
  }

  reconnectWithNewToken(): void {
    console.log('🔄 重新连接...')

    // 清理重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    // 停止心跳
    this.stopHeartbeat()

    // 🔧 关键修复：完全清理旧的 WebSocket 连接
    if (this.ws) {
      const oldState = this.ws.readyState
      console.log(`🧹 清理旧连接 (状态: ${oldState})`)
      
      // 移除所有事件监听器，避免旧连接的事件继续触发
      try {
        this.ws.onopen = null
        this.ws.onmessage = null
        this.ws.onerror = null
        this.ws.onclose = null
      } catch (e) {
        console.warn('⚠️ 移除旧连接事件监听器失败:', e)
      }
      
      // 关闭旧连接（如果还没关闭）
      if (oldState === WebSocket.OPEN || oldState === WebSocket.CONNECTING) {
        try {
          this.ws.close(1000, '重新连接')
          console.log('✅ 已关闭旧连接')
        } catch (e) {
          console.warn('⚠️ 关闭旧连接失败:', e)
        }
      }
      
      // 清空引用
      this.ws = null
    }

    // 重置状态
    this.isConnected = false
    this.isConnecting = false
    this.reconnectCount = 0
    this.connectionError = null
    this.manualClose = false

    // 延迟连接（确保旧连接完全清理）
    setTimeout(() => {
      if (!this.isConnected && !this.isConnecting) {
        this.connect()
      }
    }, 100)
  }

  /**
   * 🔧 检查并修复连接状态
   * 职责：
   * 1. 检测状态不一致（标志位 vs 实际连接状态）
   * 2. 作为心跳机制的保底检测（使用更长的超时时间）
   * 3. iOS 优化：清理可能被暂停的定时器
   */
  checkAndFixConnection(): void {
    if (this.manualClose) return

    const actualState = this.ws?.readyState
    const isActuallyConnected = actualState === WebSocket.OPEN

    // 🔧 情况1：实际未连接，但标志位显示连接中
    if (!isActuallyConnected) {
      // 重置状态标记
      if (this.isConnected) {
        console.warn('⚠️ [状态检查] 连接状态不一致，标志显示已连接但实际未连接')
        this.isConnected = false
        this.isConnecting = false
      }

      // 🔧 iOS 优化：清理可能被暂停的重连定时器
      if (this.reconnectTimer) {
        console.log('🧹 [iOS优化] 清理旧的重连定时器')
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }

      // 触发重连（避免重复重连）
      if (!this.isConnecting) {
        console.log('🔄 [状态检查] 触发重连修复连接')
        this.reconnectWithNewToken()
      }
    } else {
      // 🔧 情况2：实际已连接，但标志位显示未连接
      if (!this.isConnected) {
        console.log('ℹ️ [状态检查] 连接状态不一致，实际已连接但标志未更新，修复状态')
        this.isConnected = true
        this.isConnecting = false

        // 🔧 触发 open 事件，同步 chatStore 状态
        this.emit('open', {})
      }

      // 🔧 情况3：作为保底措施，检查心跳是否严重超时（20秒）
      // 注意：正常的心跳超时（10秒）由 startHeartbeat 负责检测
      // 这里只检测严重超时（可能心跳机制失效），作为最后的保障
      const timeSinceLastHeartbeat = Date.now() - this.lastPong
      const SEVERE_TIMEOUT = this.heartbeatTimeout * 2 // 20秒（2倍心跳超时）

      if (timeSinceLastHeartbeat > SEVERE_TIMEOUT) {
        console.warn(
          `⚠️ [状态检查] 心跳严重超时 (${Math.floor(timeSinceLastHeartbeat / 1000)}秒未收到心跳，超过${SEVERE_TIMEOUT / 1000}秒阈值)`
        )
        console.log('🔄 [状态检查] 心跳机制可能失效，触发保底重连')
        this.reconnectWithNewToken()
      } else {
        console.log(
          `✅ [状态检查] 连接正常 (心跳: ${Math.floor(timeSinceLastHeartbeat / 1000)}秒前)`
        )
      }
    }
  }

  /**
   * 🔧 iOS 优化：强制重新连接（用于从后台恢复时）
   * 清理所有可能被暂停的定时器，然后触发连接
   */
  forceReconnectAfterResume(): void {
    console.log('🔄 [iOS优化] 强制重连（从后台恢复）')

    // 清理可能被暂停的定时器
    if (this.reconnectTimer) {
      console.log('🧹 [iOS优化] 清理旧的重连定时器')
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    // 重置重连计数（iOS 后台恢复视为新的连接尝试）
    this.reconnectCount = 0

    // 检查实际连接状态
    const actualState = this.ws?.readyState
    const isActuallyConnected = actualState === WebSocket.OPEN

    if (!isActuallyConnected) {
      // 实际未连接，重置状态并重连
      this.isConnected = false
      this.isConnecting = false
      this.reconnectWithNewToken()
    } else {
      // 实际已连接，只需要同步状态
      console.log('✅ [iOS优化] 实际已连接，同步状态')
      this.isConnected = true
      this.isConnecting = false
      this.emit('open', {})
    }
  }
}

// 全局实例
let globalWebSocket: WebSocketManager | null = null

export function getWebSocketManager(): WebSocketManager {
  try {
    if (typeof window !== 'undefined') {
      const win = window as any
      if (win.$wsManager && win.$wsManager instanceof WebSocketManager) {
        globalWebSocket = win.$wsManager
        return win.$wsManager
      }
    }
  } catch {}

  if (!globalWebSocket) {
    globalWebSocket = new WebSocketManager(import.meta.env.VITE_WEBSOCKET_URL as string)
    try {
      if (typeof window !== 'undefined') (window as any).$wsManager = globalWebSocket
    } catch {}
  }

  return globalWebSocket!
}

export function getCurrentToken(): string | null {
  return tokenStorage.getToken()
}

export function hasValidToken(): boolean {
  const token = tokenStorage.getToken()
  return !!token && token !== 'e5f8297389e2ae33f6417d2b908da160'
}

export { WebSocketManager }

// 全局状态监听器
let globalStatusWatcher: NodeJS.Timeout | null = null
const WATCH_INTERVAL = 5000

export function startWebSocketStatusWatcher(): void {
  if (globalStatusWatcher) {
    console.log('⚠️ 状态监听器已在运行')
    return
  }

  if (typeof window === 'undefined') {
    console.warn('⚠️ 非浏览器环境')
    return
  }

  console.log('🔍 启动状态监听器 (每5秒)')

  globalStatusWatcher = setInterval(() => {
    try {
      const wsManager = getWebSocketManager()
      wsManager.checkAndFixConnection()
    } catch (error) {
      console.error('❌ 状态检查失败:', error)
    }
  }, WATCH_INTERVAL)

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', stopWebSocketStatusWatcher)
  }
}

export function stopWebSocketStatusWatcher(): void {
  if (globalStatusWatcher) {
    clearInterval(globalStatusWatcher)
    globalStatusWatcher = null
    console.log('🛑 停止状态监听器')
  }

  if (typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', stopWebSocketStatusWatcher)
  }
}

/**
 * 🔧 iOS 优化：调试工具 - 在控制台打印连接状态
 * 使用方法：在控制台输入 window.showWebSocketStatus()
 */
export function showWebSocketStatus(): void {
  const wsManager = getWebSocketManager()
  const stats = wsManager.getConnectionStats()
  const logs = wsManager.getErrorLogs()

  console.group('📊 WebSocket 连接状态')
  console.log(
    '✅ 连接状态:',
    stats.isConnected ? '已连接' : stats.isConnecting ? '连接中' : '未连接'
  )
  console.log('📶 连接质量:', stats.quality)
  console.log('📤 发送消息数:', stats.messagesSent)
  console.log('📥 接收消息数:', stats.messagesReceived)
  console.log('🔄 重连次数:', stats.reconnectCount)
  console.log('💓 距上次心跳:', Math.floor(stats.timeSinceLastPong / 1000), '秒')
  console.log('🌐 网络状态:', navigator.onLine ? '在线' : '离线')
  console.log('📱 User Agent:', navigator.userAgent)
  console.groupEnd()

  if (logs.length > 0) {
    console.group('📋 最近错误日志 (最多20条)')
    logs.forEach((log, index) => {
      console.log(`${index + 1}.`, log)
    })
    console.groupEnd()
  }
}

// 🔧 iOS 优化：将调试工具挂载到 window 对象
if (typeof window !== 'undefined') {
  ;(window as any).showWebSocketStatus = showWebSocketStatus
  console.log('💡 提示：可在控制台输入 showWebSocketStatus() 查看 WebSocket 连接状态')
}
